/**
 * ADR-0124 §7 — hub Learning page (cross-tenant).
 *
 * Same sections as the tenant page over hub-level aggregates: rules
 * promoted, and needs-attention (advisory eval failures).
 */

import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import {
  LearningFunnelCard,
  NeedsAttentionSection,
  type LearningFunnel,
  type NeedsAttention,
} from "../components/LearningSections.js";

export function AdminLearning() {
  const [funnel, setFunnel] = useState<LearningFunnel | null>(null);
  const [needsAttention, setNeedsAttention] = useState<NeedsAttention | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [f, na] = await Promise.all([
        apiFetch<{ funnel: LearningFunnel }>(`/api/admin/ai-knowledge/learning/funnel?days=${days}`),
        apiFetch<NeedsAttention>("/api/admin/ai-knowledge/learning/needs-attention"),
      ]);
      setFunnel(f.funnel);
      setNeedsAttention(na);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageLayout
      title="Learning"
      actions={
        <select className="select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      }
    >
      {error ? (
        <ErrorState detail={error} />
      ) : loading && !funnel ? (
        <LoadingState />
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {funnel && <LearningFunnelCard funnel={funnel} days={days} />}
          {needsAttention && <NeedsAttentionSection data={needsAttention} />}
        </div>
      )}
    </PageLayout>
  );
}
